/**
 * L'aiguillage par nom d'hôte (C26 SP3, spec § 0.1 ; cadrage § 9) — module pur.
 *
 * La façade est choisie par l'hôte : un hôte de `REACT_APP_AZARIA_HOSTS`
 * (liste séparée par des virgules, comparée en minuscules) ouvre Azaria ;
 * l'organisation vient du chemin `/o/<slug>`. En local seulement (`localhost`,
 * `127.0.0.1`), `?facade=azaria&o=<slug>` force la façade. Sans variable, ou
 * sur tout autre hôte, c'est Anagogia, inchangé.
 *
 * `FACADE` est résolue une fois au démarrage et jamais réévaluée : un hôte ne
 * change pas sans rechargement.
 */

export type Facade = 'anagogia' | 'azaria';

export interface FacadeResolue {
  facade: Facade;
  /** L'adresse de l'organisation demandée (`/o/<slug>`), ou `null`. */
  slug: string | null;
}

export const AZARIA_HOSTS_ENV = 'REACT_APP_AZARIA_HOSTS';
export const FACADE_NAME_ENV = 'REACT_APP_FACADE_NAME';

/** Même règle que l'adresse d'une organisation au serveur (`SLUG_RE`). */
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

export type EnvFacade = Record<string, string | undefined>;

/** Les hôtes de la façade, en minuscules, sans blancs ; vide sans variable. */
export function azariaHosts(env: EnvFacade): string[] {
  return (env[AZARIA_HOSTS_ENV] ?? '')
    .split(',')
    .map(h => h.trim().toLowerCase())
    .filter(Boolean);
}

/** `/o/<slug>` ou `/o/<slug>/…` → le slug ; sinon `null`. */
export function slugDuChemin(pathname: string): string | null {
  const m = /^\/o\/([^/?#]+)/.exec(pathname || '');
  if (!m) return null;
  const slug = decodeURIComponent(m[1]).toLowerCase();
  return SLUG_RE.test(slug) ? slug : null;
}

/** Le chemin de la façade pour une adresse d'organisation. */
export function cheminDeLOrganisation(slug: string): string {
  return `/o/${slug}`;
}

export function estHoteLocal(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1' || h.endsWith('.localhost');
}

export function resolveFacade(hostname: string, search: string, pathname: string, env: EnvFacade): FacadeResolue {
  const hote = (hostname || '').toLowerCase();
  const duChemin = slugDuChemin(pathname);
  if (azariaHosts(env).includes(hote)) {
    return { facade: 'azaria', slug: duChemin };
  }
  if (estHoteLocal(hote)) {
    const params = new URLSearchParams(search || '');
    if (params.get('facade') === 'azaria') {
      const o = (params.get('o') ?? '').trim().toLowerCase();
      return { facade: 'azaria', slug: duChemin ?? (SLUG_RE.test(o) ? o : null) };
    }
  }
  return { facade: 'anagogia', slug: null };
}

/**
 * L'adresse de retour de la connexion (spec § 0.2) : l'origine courante sous la
 * façade (la personne revient là où elle était), la variable du build sinon.
 */
export function redirectUriPour(facade: Facade, env: EnvFacade, origin: string, variable: string): string {
  if (facade === 'azaria') return origin;
  return env[variable] || origin;
}

/**
 * Le nom affiché du produit : celui de la façade sous Azaria (variable du build,
 * « Azaria » par défaut), « Anagogia » sinon. Les pages communes aux deux
 * produits (accueil de connexion, invitation, consentement) le lisent pour ne
 * jamais nommer l'autre produit.
 */
export function nomDuProduit(facade: Facade, env: EnvFacade): string {
  if (facade !== 'azaria') return 'Anagogia';
  return (env[FACADE_NAME_ENV] ?? '').trim() || 'Azaria';
}

function depuisLaFenetre(): FacadeResolue {
  if (typeof window === 'undefined' || !window.location) return { facade: 'anagogia', slug: null };
  const { hostname, search, pathname } = window.location;
  return resolveFacade(hostname, search, pathname, process.env as EnvFacade);
}

/** La façade de cette page, lue une fois au démarrage. */
export const FACADE: FacadeResolue = depuisLaFenetre();

/** Le nom affiché du produit de cette page (voir `nomDuProduit`). */
export const NOM_DU_PRODUIT: string = nomDuProduit(FACADE.facade, process.env as EnvFacade);

/** L'hôte d'Anagogia (liste séparée par des virgules, le premier compte) : « Mes notes » s'y ouvre depuis la façade (C26 SP4). */
export const APPLICATION_HOST_ENV = 'REACT_APP_APPLICATION_HOST';
